import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Skeleton } from "antd";
import { xml2js } from "xml-js";
import { UserDataContext, UiTextsContext } from "../../../contexts/Contexts";
import {
  notifySavedRecommendationService,
  saveRecomendationService,
  getListPlacesOfInterestService,
} from "../../../../infrastructure/services/PublicationService";
import SaveRecommendation from "../../publications/actions/SaveRecommendation";
import { useMediaQuery } from "react-responsive";
import { showErrorMessage, showSuccessMessage } from "../../utils/messages";
import { getLocalItem } from "../../../../infrastructure/storage/LocalStorage";
import { userInfoLL } from "../../../../infrastructure/storage/LocalStorageIds";

import "./styles/recommendationsNew.css";

const SAVED_RECOMMENDATIONS_STORAGE_EVENT =
  "vivanomada:saved-recommendations-changed";

const notifySavedRecommendationsChanged = (detail) => {
  if (typeof window === "undefined") return;

  window.dispatchEvent(
    new CustomEvent(SAVED_RECOMMENDATIONS_STORAGE_EVENT, { detail })
  );

  localStorage.setItem(
    SAVED_RECOMMENDATIONS_STORAGE_EVENT,
    JSON.stringify({
      ...detail,
      timestamp: Date.now(),
    })
  );
};

const Recommendations = ({ loadingRecomendations }) => {
  const isLaptop = useMediaQuery({ maxWidth: 1212 });
  const isMobile = useMediaQuery({ maxWidth: 992 });

  const { userData } = useContext(UserDataContext);
  const { texts } = useContext(UiTextsContext);

  const [loading, setLoading] = useState(false);
  const [saveLoading, setSaveLoading] = useState(null);
  const [recommendationsList, setRecommendationsList] = useState([]);
  const [savedStates, setSavedStates] = useState({});

  const [currentIdx, setCurrentIdx] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const hoverRef = useRef(null);

  const [reduceMotion, setReduceMotion] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const storedUserInfo = getLocalItem(userInfoLL);
  const resolvedUserId =
    userData?.idUsuario || storedUserInfo?.idUsuario || null;

  useEffect(() => {
    if (!resolvedUserId) return;

    const fetchRecomendaciones = async () => {
      setLoading(true);
      try {
        const xmlString = await getListPlacesOfInterestService(resolvedUserId);
        const result = xml2js(xmlString, {
          compact: true,
          ignoreComment: true,
        });

        const items = result.Recomendaciones?.Recomendacion;
        if (!items) {
          setRecommendationsList([]);
          setCurrentIdx(0);
          return;
        }

        const arr = Array.isArray(items) ? items : [items];
        const formatted = arr.map((item) => ({
          "@id": item.idRecomendaciones._text,
          "@categoria": item.Categoria._text,
          "@nombre": item.Nombre._text,
          descripcion: item.Descripcion._text,
          fechaRegistro: item.FechaRegistro._text,
          urlLugar:
            item.urlLugar._attributes?.["xsi:nil"] === "true"
              ? null
              : item.urlLugar._text,
          urlImage:
            item.urlImage._attributes?.["xsi:nil"] === "true"
              ? null
              : item.urlImage._text,
          guardado: item.guardado._text === "1",
        }));

        setRecommendationsList(formatted);
        setCurrentIdx(0);
      } catch (err) {
        // Opcional: showErrorMessage(texts?.LB0542);
      } finally {
        setLoading(false);
      }
    };

    fetchRecomendaciones();
  }, [resolvedUserId]);

  const shuffledDesktop = useMemo(() => {
    if (isMobile) return recommendationsList;
    const arr = [...recommendationsList];
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }, [recommendationsList, isMobile]);

  useEffect(() => {
    setCurrentIdx(0);
  }, [shuffledDesktop.length]);
  useEffect(() => {
    if (isMobile) return;
    const onVis = () => setIsPaused(document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [isMobile]);

  useEffect(() => {
    if (isMobile || reduceMotion) return;
    const total = shuffledDesktop.length;
    if (total <= 1) return;
    if (isPaused) return;

    const current = shuffledDesktop[currentIdx];
    const descLen = (current?.descripcion || "").length;
    const intervalMs = descLen > 160 ? 11000 : 8000;

    const id = setTimeout(() => {
      setCurrentIdx((i) => (i + 1) % total);
    }, intervalMs);

    return () => clearTimeout(id);
  }, [isMobile, reduceMotion, isPaused, shuffledDesktop, currentIdx]);

  const saveRecomendations = async (userId, recommendation) => {
    const recId = recommendation?.["@id"];
    if (!recId) return;

    setSaveLoading(recId);
    try {
      const formData = new FormData();
      formData.append("jsonValue", JSON.stringify(recommendation));

      const { message } = await saveRecomendationService(userId, formData);

      const nowSaved = !message.includes("desmarcado");

      notifySavedRecommendationService({
        userId,
        recommendationId: String(recId),
        saved: nowSaved,
      }).catch((error) => {
        console.error("Error notificando recomendación guardada", error);
      });

      setSavedStates((prev) => ({
        ...prev,
        [recId]: nowSaved,
      }));
      setRecommendationsList((prev) =>
        prev.map((item) =>
          item["@id"] === recId ? { ...item, guardado: nowSaved } : item
        )
      );

      showSuccessMessage(
        nowSaved
          ? texts?.LB0709 || "Recomendación guardada correctamente"
          : texts?.LB0710 || "Recomendación removida correctamente"
      );

      notifySavedRecommendationsChanged({
        id: recId,
        saved: nowSaved,
        userId,
      });
    } catch (e) {
      console.error("Error guardando recomendación", e);
      showErrorMessage(
        texts?.ER0088 || "Error al guardar, por favor intenta más tarde."
      );
    } finally {
      setSaveLoading(null);
    }
  };

  const renderCard = (rec) => (
    <div key={rec["@id"]}>
      <article className="rec-card rec-card--fade">
        <div className="rec-card__media">
          <img
            alt={rec["@nombre"]}
            src={rec.urlImage || "/img/properties/casa.png"}
            className="rec-card__img"
          />
        </div>

        <div className="rec-card__body">
          <h3 className="rec-card__title">{rec["@nombre"]}</h3>

          <div className="rec-card__actions">
            <a
              href={rec.urlLugar || "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="rec-card__link"
            >
              {!isLaptop ? texts?.LB0170 || "Go" : "Go"}
            </a>

            <SaveRecommendation
              onClick={() => saveRecomendations(resolvedUserId, rec)}
              loading={saveLoading === rec["@id"]}
              saved={savedStates[rec["@id"]] ?? rec.guardado}
            />
          </div>
        </div>
      </article>
    </div>
  );

  return (
    <section className="recs">
      <h2 className="home-side-title-mc recs__title">
        {texts?.LB0063 || "Recommendations"}
      </h2>

      {loading ? (
        <div className={`recs__list ${isMobile ? "is-row" : "is-col"}`}>
          {isMobile ? (
            Array(3)
              .fill(null)
              .map((_, i) => (
                <div className="rec-card rec-card--loading" key={i}>
                  <Skeleton
                    active
                    avatar={{ shape: "square", size: isMobile ? 77 : 250 }}
                    title
                    paragraph={{ rows: 1 }}
                  />
                </div>
              ))
          ) : ( 
            <div className="rec-card rec-card--loading">
              <Skeleton
                active
                avatar={{ shape: "square", size: 250 }}
                title
                paragraph={{ rows: 1 }}
              />
            </div>
          )}
        </div>
      ) : (
        <>
          <div
            className={`recs__list ${isMobile ? "is-row" : "is-col"}`}
            ref={hoverRef}
            onMouseEnter={() => !isMobile && setIsPaused(true)}
            onMouseLeave={() => !isMobile && setIsPaused(false)}
          >
            {isMobile
              ? recommendationsList.map((rec) => renderCard(rec))
              : shuffledDesktop[currentIdx]
              ? renderCard(shuffledDesktop[currentIdx])
              : null}
          </div>
        </>
      )}
    </section>
  );
};

export default Recommendations;
